import {
  hasDrawingMatch,
  hasDrawings,
  matches,
  matchesDoc,
  wbsLabel,
} from "./treeLabels";
import { isSoftwareWbs, treeGroupKey, wbsGroupNodeId } from "./wbsGroups";
import { sectionItemNodeId, sectionNodeId } from "./treeNodeIds";
import { designAutomationItemForWbs } from "./treeConstants";

export const SEARCH_DEBOUNCE_MS = 400;

// Open just enough to put each match on screen, and no more: the plant and
// the discipline folder above a matching WBS. Deliberately NOT expanded:
// - a plant whose own name matched, since the match is the plant row itself
//   and expanding it would unfurl every discipline and every WBS under it;
// - the matching WBS node, whose contents nobody asked to see.
// The tree is already filtered to matches, so anything worth opening is one
// click away.
export const buildAutoExpandIds = ({
  plants,
  query,
  wbsByPlant,
  docsByWbs,
  drawingsByWbs,
}) => {
  if (!query) return null;
  const ids = new Set();

  plants.forEach((plant) => {
    const wbsList = wbsByPlant[plant.id];
    if (!Array.isArray(wbsList)) return;

    wbsList.forEach((wbs) => {
      // A WBS the tree lists nowhere has nothing to open to. Those written
      // under a legacy type are listed - under Design Automation (see
      // designAutomationItemForWbs) - so only the ones that land in neither
      // place are skipped.
      const automationItem = designAutomationItemForWbs(wbs);
      if (
        isSoftwareWbs(wbs) &&
        !automationItem &&
        !hasDrawings(wbs, drawingsByWbs)
      ) {
        return;
      }
      const wbsMatches = matches(wbsLabel(wbs), query);
      const docs = docsByWbs[wbs.id];
      const hasDocMatch =
        Array.isArray(docs) && docs.some((doc) => matchesDoc(doc, query));

      if (
        wbsMatches ||
        hasDocMatch ||
        hasDrawingMatch(wbs, query, drawingsByWbs)
      ) {
        ids.add(`plant-${plant.id}`);
        // Whatever sits between the plant and this WBS has to open, or the
        // match stays hidden inside it: the Design Automation section and
        // the row holding it, or the Estimation section and the discipline
        // folder under that.
        if (automationItem) {
          ids.add(sectionNodeId(plant.id, "design-automation"));
          ids.add(
            sectionItemNodeId(plant.id, "design-automation", automationItem.key),
          );
        } else {
          ids.add(sectionNodeId(plant.id, "estimation"));
          ids.add(wbsGroupNodeId(plant.id, treeGroupKey(wbs)));
        }
      }
    });
  });

  return Array.from(ids);
};

export const isPlantVisible = (
  plant,
  query,
  wbsByPlant,
  docsByWbs,
  drawingsByWbs,
) => {
  if (!query) return true;
  if (matches(plant.name || "", query)) return true;
  const wbsList = wbsByPlant[plant.id];
  if (!Array.isArray(wbsList)) return false;
  // Mirrors where the tree lists a WBS: under its discipline in Estimation,
  // or - for a legacy type - under Design Automation. One listed in neither
  // counts only if it carries 2D drawings, which the picker still offers.
  return wbsList.some(
    (wbs) =>
      (!isSoftwareWbs(wbs) ||
        designAutomationItemForWbs(wbs) ||
        hasDrawings(wbs, drawingsByWbs)) &&
      isWbsVisible(plant, wbs, query, docsByWbs, drawingsByWbs),
  );
};

export const isWbsVisible = (plant, wbs, query, docsByWbs, drawingsByWbs) => {
  if (!query) return true;
  if (matches(plant.name || "", query)) return true;
  if (matches(wbsLabel(wbs), query)) return true;
  if (hasDrawingMatch(wbs, query, drawingsByWbs)) return true;
  const docs = docsByWbs[wbs.id];
  return Array.isArray(docs) && docs.some((doc) => matchesDoc(doc, query));
};

export const isDocVisible = (plant, wbs, doc, query) => {
  if (!query) return true;
  if (matches(plant.name || "", query)) return true;
  if (matches(wbsLabel(wbs), query)) return true;
  return matchesDoc(doc, query);
};
